import type {
  QuestionnaireDraft,
  QuestionnaireValidationIssue,
} from "@digital-twins/shared-types";

/**
 * The builder's unsaved edits, kept in `sessionStorage` per draft (B4), so a
 * reload, Back or Forward, or leaving and returning in the same tab brings
 * them back. Only Save writes the draft: this is never a server write.
 *
 * The entry is the workspace's own pair: the accepted draft the edits started
 * from (`base`) and the edited copy (`local`), with the newest revision the
 * tab knew. A restore keeps `local` on `base`, as edits under a newer draft
 * always stay: the next Save applies them to the draft served now (L12).
 *
 * Storage can throw (private mode, a full quota, cleared site data): every
 * read and write here is guarded, and a failed one keeps nothing rather than
 * breaking the builder.
 */
export interface UnsavedDraftEntry {
  baseRevision: number | null;
  /** The accepted draft the edits started from. */
  base: QuestionnaireDraft;
  local: QuestionnaireDraft;
  /**
   * The refused Save's issues, with the fingerprint of the draft it refused
   * (L3): a restore shows them again only on that same draft (E-session-11).
   */
  refusal?: StoredSaveRefusal;
}

/** A refused Save's issues as stored: each map as its entries. */
export interface StoredSaveRefusal {
  fingerprint: string;
  byQuestionId: Array<[string, Array<QuestionnaireValidationIssue>]>;
  byAssetId: Array<[string, Array<QuestionnaireValidationIssue>]>;
  unscoped: Array<QuestionnaireValidationIssue>;
}

const KEY_PREFIX = "questionnaire-unsaved:";

function keyOf(draftId: string): string {
  return `${KEY_PREFIX}${draftId}`;
}

/**
 * Keep the edits of `local` over `baseSnapshot` (the workspace's accepted
 * draft, already serialized) at `baseRevision`, with the refusal of the last
 * Save when there is one.
 */
export function writeUnsavedDraft(
  draftId: string,
  baseRevision: number | null,
  baseSnapshot: string,
  local: QuestionnaireDraft,
  refusal: StoredSaveRefusal | null = null,
): void {
  try {
    window.sessionStorage.setItem(
      keyOf(draftId),
      `{"baseRevision":${JSON.stringify(baseRevision)},"base":${baseSnapshot},"local":${JSON.stringify(local)}${
        refusal ? `,"refusal":${JSON.stringify(refusal)}` : ""
      }}`,
    );
  } catch {
    // Storage unavailable or full: the edits stay on the page only.
  }
}

/**
 * The stored entry for `draftId`, or null when there is none or it cannot be
 * read as one (it is then removed).
 */
export function readUnsavedDraft(draftId: string): UnsavedDraftEntry | null {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(keyOf(draftId));
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<UnsavedDraftEntry> | null;
    if (
      parsed &&
      (typeof parsed.baseRevision === "number" || parsed.baseRevision === null) &&
      parsed.base &&
      typeof parsed.base === "object" &&
      (parsed.base as { id?: unknown }).id === draftId &&
      parsed.local &&
      typeof parsed.local === "object" &&
      (parsed.local as { id?: unknown }).id === draftId
    ) {
      const { refusal, ...entry } = parsed as UnsavedDraftEntry;
      return isStoredRefusal(refusal) ? { ...entry, refusal } : entry;
    }
  } catch {
    // Not an entry this build wrote.
  }
  clearUnsavedDraft(draftId);
  return null;
}

export function clearUnsavedDraft(draftId: string): void {
  try {
    window.sessionStorage.removeItem(keyOf(draftId));
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

function isStoredRefusal(value: unknown): value is StoredSaveRefusal {
  const refusal = value as Partial<StoredSaveRefusal> | null | undefined;
  return (
    typeof refusal?.fingerprint === "string" &&
    Array.isArray(refusal.byQuestionId) &&
    Array.isArray(refusal.byAssetId) &&
    Array.isArray(refusal.unscoped)
  );
}
